import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Env, ENV_COLORS } from '../shared';
import { FlaggedUsersPanel } from './panels/FlaggedUsersPanel';
import { ReportsPanel } from './panels/ReportsPanel';

type SubTab = 'flagged' | 'reports';

const SUB_TABS: { id: SubTab; label: string; blurb: string; prodWarning: React.ReactNode }[] = [
  {
    id: 'flagged',
    label: 'Flagged users',
    blurb:
      'Everyone currently blocked from earning by a fraud flag. Nothing expires a flag — it stays until someone clears it here.',
    prodWarning: <>Acting on <b>production</b>. Unflagging restores a live user's earning immediately.</>,
  },
  {
    id: 'reports',
    label: 'Reports',
    blurb:
      'User reports from the app. Same-day for safety and under-18 reports, one business day for the rest. Every action needs a note.',
    prodWarning: (
      <>
        Acting on <b>production</b>. Removing someone from the stranger list applies to a live user immediately.
      </>
    ),
  },
];

const isSub = (v: string | null): v is SubTab => SUB_TABS.some((t) => t.id === v);

// The admin backend serves the report queue on prod and /dev only; staging is
// a backup of prod that the apps never use, so the Reports panel omits it.
const ENVS_BY_SUB: Record<SubTab, Env[]> = {
  flagged: ['dev', 'staging', 'prod'],
  reports: ['dev', 'prod'],
};

/**
 * Users admin tab: the flagged-user review queue and the user report queue
 * (SSA-1579). `?sub=flagged|reports` deep-links a panel.
 *
 * Separate from the Gamification tab even though flags read the same records:
 * being flagged or reported is account-level support work rather than economy
 * tuning. Defaults to prod for the same reason Gamification does — a responder
 * should land on live data.
 */
export function UsersTab() {
  const [env, setEnv] = useState<Env>('prod');
  const requestedSub = useSearchParams()[0].get('sub');
  const navigationKey = useLocation().key;
  const [sub, setSub] = useState<SubTab>(isSub(requestedSub) ? requestedSub : 'flagged');
  useEffect(() => {
    if (isSub(requestedSub)) setSub(requestedSub);
  }, [requestedSub, navigationKey]);

  const envs = ENVS_BY_SUB[sub];
  // Switching to a panel that doesn't serve the selected env lands on prod.
  useEffect(() => {
    if (!envs.includes(env)) setEnv('prod');
  }, [envs, env]);

  const active = SUB_TABS.find((t) => t.id === sub) ?? SUB_TABS[0];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                sub === t.id
                  ? 'bg-violet-950/40 text-violet-300 border border-violet-500/60'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Environment</span>
          <div className="flex gap-1">
            {envs.map((e) => (
              <button
                key={e}
                onClick={() => setEnv(e)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md border uppercase ${
                  env === e ? ENV_COLORS[e].pill : 'bg-gray-900 border-gray-800 text-gray-500 hover:text-gray-300'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500">{active.blurb}</p>

      {env === 'prod' && (
        <div className="px-3 py-2 bg-red-950/30 border border-red-900/40 rounded-lg text-xs text-red-300">
          {active.prodWarning}
        </div>
      )}

      {sub === 'flagged' && <FlaggedUsersPanel env={env} />}
      {sub === 'reports' && envs.includes(env) && <ReportsPanel env={env} />}
    </div>
  );
}
